import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumn,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { FlexGridXlsxConverter } from "@grapecity/wijmo.grid.xlsx";
import { useEffect, useRef, useState } from "react";
import excel from "src/assets/images/excelIcon.png";
import SimpleColumnPicker from "src/qubCommon/Grids/SimpleColumnPicker";
import {
  formatFloat,
  showFloat,
} from "src/qubCommon/wijmoHelpers/numberMethods";

const rowActionButtonSx = {
  width: 26,
  height: 26,
};

const REVIEW_COLUMN_CLASS = "expense-review-column";
const REVIEW_COLUMN_HEADER_CLASS = "expense-review-column-header";
const REVIEW_COLUMN_BINDINGS = new Set([
  "expStatusAbbr",
  "approvedAmount",
  "statusComments",
]);

export const REVIEW_STATUS_IDS = {
  pending: 125,
  approved: 150,
  partiallyApproved: 170,
  rejected: 190,
};

export const REVIEW_STATUS_OPTIONS = [
  { value: REVIEW_STATUS_IDS.pending.toString(), label: "Προς έλεγχο" },
  { value: REVIEW_STATUS_IDS.approved.toString(), label: "Εγκεκριμένη" },
  {
    value: REVIEW_STATUS_IDS.partiallyApproved.toString(),
    label: "Μερικώς εγκεκριμένη",
  },
  { value: REVIEW_STATUS_IDS.rejected.toString(), label: "Απορριφθείσα" },
];

const reviewInputSx = {
  "& .MuiInputBase-root": {
    height: 24,
    minHeight: 24,
    fontSize: 13,
  },
  "& .MuiInputBase-input": {
    px: 0.75,
    py: 0.35,
  },
};

const reviewGridSx = {
  "& .wj-flexgrid .wj-cell.expense-review-column": {
    backgroundColor: "#fff7ed",
    borderColor: "#fed7aa",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column-header": {
    backgroundColor: "#ffedd5",
    borderColor: "#fb923c",
    color: "#9a3412",
    fontWeight: 800,
  },
  "& .wj-flexgrid .wj-cell.expense-review-column.wj-state-selected": {
    backgroundColor: "#fdba74",
    color: "#431407",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column .MuiInputBase-root": {
    backgroundColor: "transparent",
  },
};

const stopGridEvent = (event) => {
  event.stopPropagation();
};

const normalizeStatusValue = (value) =>
  value === undefined || value === null || value === ""
    ? REVIEW_STATUS_IDS.pending.toString()
    : value.toString();

const isHiddenMode = (value) =>
  value?.toString?.().trim?.().toLowerCase?.() === "hidden";

const isTrueValue = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const statusLabelByValue = REVIEW_STATUS_OPTIONS.reduce(
  (acc, option) => ({
    ...acc,
    [option.value]: option.label,
  }),
  {}
);

const parseAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (!normalized) return null;

  if (
    normalized.includes(",") ||
    /^(\d{1,3}\.)+\d{3}$/.test(normalized)
  ) {
    const amount = formatFloat(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (value) => {
  const amount = parseAmount(value);
  return amount === null ? "" : showFloat(amount);
};

const ReadOnlyCell = ({ value, align = "left", muted = false }) => (
  <Box
    sx={{
      alignItems: "center",
      color: muted ? "#667085" : "#111827",
      display: "flex",
      fontSize: 13,
      fontWeight: muted ? 600 : 700,
      height: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      overflow: "hidden",
      textAlign: align,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={value ?? ""}
  >
    {value || "-"}
  </Box>
);

const REVIEW_EDITOR_SELECTOR = "[data-expense-review-editor='true']";

const getReviewEditors = (grid, currentInput) => {
  const hostElement =
    grid?.hostElement ?? currentInput?.closest?.(".wj-flexgrid");

  if (!hostElement) return [];

  return Array.from(hostElement.querySelectorAll(REVIEW_EDITOR_SELECTOR))
    .filter((editor) => !editor.disabled && editor.offsetParent !== null)
    .sort((firstEditor, secondEditor) => {
      const firstOrder = Number(firstEditor.dataset.expenseReviewEditorOrder);
      const secondOrder = Number(secondEditor.dataset.expenseReviewEditorOrder);

      return firstOrder - secondOrder;
    });
};

const focusReviewEditor = (editor) => {
  if (!editor) return;

  editor.focus?.();

  if (editor.dataset.expenseReviewEditorType === "approvedAmount") {
    editor.select?.();
  }
};

const moveToNextReviewEditor = (currentInput, grid) => {
  const editors = getReviewEditors(grid, currentInput);
  const currentIndex = editors.indexOf(currentInput);
  const nextEditor = currentIndex >= 0 ? editors[currentIndex + 1] : null;
  const currentOrder = Number(currentInput?.dataset.expenseReviewEditorOrder);

  window.requestAnimationFrame(() => {
    if (nextEditor?.isConnected) {
      focusReviewEditor(nextEditor);
      return;
    }

    const freshEditors = getReviewEditors(grid, currentInput);
    const fallbackEditor = freshEditors.find((editor) => {
      const editorOrder = Number(editor.dataset.expenseReviewEditorOrder);
      return editorOrder > currentOrder;
    });

    focusReviewEditor(fallbackEditor);
  });
};

const finishReviewEditing = ({ grid, input, moveNext = false }) => {
  if (moveNext) {
    moveToNextReviewEditor(input, grid);
  }

  grid?.finishEditing?.();
  input?.blur?.();
};

const ApprovedAmountEditor = ({ value, onCommit, grid, editorOrder }) => {
  const inputRef = useRef(null);
  const skipNextBlurCommitRef = useRef(false);
  const [draftValue, setDraftValue] = useState(formatAmount(value));

  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    setDraftValue(formatAmount(value));
  }, [value]);

  const commitValue = () => {
    const amount = parseAmount(draftValue);
    const nextValue = amount === null ? "" : showFloat(amount);

    setDraftValue(nextValue);
    onCommit?.(nextValue);
  };

  const selectWholeValue = (event) => {
    stopGridEvent(event);
    window.requestAnimationFrame(() => {
      inputRef.current?.select?.();
    });
  };

  return (
    <TextField
      fullWidth
      variant="standard"
      size="small"
      value={draftValue}
      inputRef={inputRef}
      onChange={(event) => setDraftValue(event.target.value)}
      onBlur={() => {
        if (skipNextBlurCommitRef.current) {
          skipNextBlurCommitRef.current = false;
          return;
        }

        commitValue();
      }}
      onFocus={selectWholeValue}
      onClick={selectWholeValue}
      onMouseDown={stopGridEvent}
      onKeyDown={(event) => {
        stopGridEvent(event);

        if (event.key === "Enter") {
          event.preventDefault();
          skipNextBlurCommitRef.current = true;
          commitValue();
          finishReviewEditing({
            grid,
            input: inputRef.current,
            moveNext: true,
          });
        }

        if (event.key === "Escape") {
          event.preventDefault();
          skipNextBlurCommitRef.current = true;
          setDraftValue(formatAmount(value));
          finishReviewEditing({ grid, input: inputRef.current });
        }
      }}
      InputProps={{
        disableUnderline: true,
        inputProps: {
          "data-expense-review-editor": "true",
          "data-expense-review-editor-order": editorOrder,
          "data-expense-review-editor-type": "approvedAmount",
          style: { textAlign: "right" },
        },
      }}
      sx={reviewInputSx}
    />
  );
};

const ReviewCommentsEditor = ({ value, onCommit, grid, editorOrder }) => {
  const inputRef = useRef(null);
  const skipNextBlurCommitRef = useRef(false);
  const [draftValue, setDraftValue] = useState(value ?? "");

  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    setDraftValue(value ?? "");
  }, [value]);

  const commitValue = () => {
    onCommit?.(draftValue ?? "");
  };

  return (
    <TextField
      fullWidth
      variant="standard"
      size="small"
      value={draftValue}
      inputRef={inputRef}
      onChange={(event) => setDraftValue(event.target.value)}
      onBlur={() => {
        if (skipNextBlurCommitRef.current) {
          skipNextBlurCommitRef.current = false;
          return;
        }

        commitValue();
      }}
      onClick={stopGridEvent}
      onMouseDown={stopGridEvent}
      onKeyDown={(event) => {
        stopGridEvent(event);

        if (event.key === "Enter") {
          event.preventDefault();
          skipNextBlurCommitRef.current = true;
          commitValue();
          finishReviewEditing({
            grid,
            input: inputRef.current,
            moveNext: true,
          });
        }

        if (event.key === "Escape") {
          event.preventDefault();
          skipNextBlurCommitRef.current = true;
          setDraftValue(value ?? "");
          finishReviewEditing({ grid, input: inputRef.current });
        }
      }}
      InputProps={{
        disableUnderline: true,
        inputProps: {
          "data-expense-review-editor": "true",
          "data-expense-review-editor-order": editorOrder,
          "data-expense-review-editor-type": "statusComments",
        },
      }}
      sx={reviewInputSx}
    />
  );
};

const ExpenseDetailsGrid = ({
  rows = [],
  canEdit = false,
  showApprovedAmount = false,
  showReviewStatus = false,
  canReview = false,
  modeConfig = {},
  addExpenseSetting = null,
  isAddExpenseDisabled = false,
  onAddExpense,
  onRowAction,
  onReviewRowChange,
}) => {
  const gridRef = useRef(null);
  const [columnPickerRefreshKey, setColumnPickerRefreshKey] = useState(0);
  const showAppartmentColumn = !isHiddenMode(modeConfig.appartmentMode);
  const showChildColumn = !isHiddenMode(modeConfig.childMode);
  const showEmployeeColumn = !isHiddenMode(modeConfig.employeeMode);
  const showDateFromColumn = !isHiddenMode(modeConfig.dateFromMode);
  const showDateToColumn = !isHiddenMode(modeConfig.dateToMode);
  const showVatColumn = !isTrueValue(modeConfig.hideVat);
  const canAddExpense = addExpenseSetting?.can === true;
  const addExpenseText = addExpenseSetting?.txt ?? "Προσθήκη Δαπάνης";

  useEffect(() => {
    if (!gridRef.current) return;

    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  }, [
    canEdit,
    showAppartmentColumn,
    showApprovedAmount,
    showChildColumn,
    showDateFromColumn,
    showDateToColumn,
    showEmployeeColumn,
    showReviewStatus,
    showVatColumn,
  ]);

  const handleGridInitialized = (grid) => {
    gridRef.current = grid;
    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  };

  const handleGridFormatItem = (grid, event) => {
    const binding = event.panel?.columns?.[event.col]?.binding;

    if (!REVIEW_COLUMN_BINDINGS.has(binding)) return;

    event.cell.classList.add(REVIEW_COLUMN_CLASS);

    if (event.panel === grid.columnHeaders) {
      event.cell.classList.add(REVIEW_COLUMN_HEADER_CLASS);
    }
  };

  const handleExportExcel = () => {
    if (!gridRef.current) return;

    FlexGridXlsxConverter.saveAsync(
      gridRef.current,
      {
        includeColumnHeaders: true,
        includeStyles: false,
      },
      "ExpenseDetails.xlsx",
    );
  };

  const handleStatusChange = (row, value) => {
    const statusId = value ? Number(value) : null;
    const patch = {
      expStatusId: statusId,
      expStatusAbbr: statusLabelByValue[value] ?? "",
    };

    if (statusId !== REVIEW_STATUS_IDS.partiallyApproved) {
      patch.approvedAmount = 0;
    }

    onReviewRowChange?.(row.id, patch);
  };

  const handleApprovedAmountCommit = (row, value) => {
    onReviewRowChange?.(row.id, { approvedAmount: value });
  };

  const handleReviewCommentsChange = (row, value) => {
    onReviewRowChange?.(row.id, { statusComments: value });
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: "0.25rem",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        p: 1,
        ...reviewGridSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <SimpleColumnPicker
          ref={gridRef}
          refreshKey={columnPickerRefreshKey}
        />
        <Box boxShadow={2} sx={{ borderRadius: 50, width: 40, height: 40 }}>
          <Tooltip title="Εξαγωγή σε Excel">
            <IconButton onClick={handleExportExcel}>
              <img src={excel} alt="excel" style={{ height: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {canAddExpense && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            disabled={isAddExpenseDisabled}
            onClick={() => onAddExpense?.()}
            title={`${addExpenseText} (Alt+N)`}
            sx={{
              fontWeight: 800,
              minHeight: 32,
              whiteSpace: "nowrap",
            }}
          >
            {addExpenseText}
          </Button>
        )}
      </Box>

      <FlexGrid
        autoGenerateColumns={false}
        className="explorer-grid"
        formatItem={handleGridFormatItem}
        headersVisibility="Column"
        initialized={handleGridInitialized}
        isReadOnly
        itemsSource={rows}
        selectionMode="Row"
      >
        <FlexGridFilter />
        {canEdit && (
          <FlexGridColumn
            header=""
            width={78}
            minWidth={78}
            align="center"
            allowSorting={false}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                    height: "100%",
                  }}
                >
                  <Tooltip title="Επεξεργασία">
                    <IconButton
                      size="small"
                      color="primary"
                      sx={rowActionButtonSx}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowAction?.("edit", ctx.item);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Διαγραφή">
                    <IconButton
                      size="small"
                      color="error"
                      sx={rowActionButtonSx}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowAction?.("delete", ctx.item);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            />
          </FlexGridColumn>
        )}
        <FlexGridColumn
          binding="expTypeName"
          header="Είδος Δαπάνης"
          width={220}
          minWidth={180}
        />
        <FlexGridColumn
          binding="netValue"
          header="Αξία"
          format="c€"
          width={130}
          align="right"
        />
        {showVatColumn && (
          <FlexGridColumn
            binding="vatValue"
            header="ΦΠΑ"
            format="c€"
            width={130}
            align="right"
          />
        )}
        <FlexGridColumn
          binding="totalAmount"
          header="Σύνολο"
          format="c€"
          width={140}
          align="right"
        />
        {showReviewStatus && (
          <FlexGridColumn
            binding="expStatusAbbr"
            header="Κατάσταση Ελέγχου"
            width={190}
            minWidth={170}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const value = normalizeStatusValue(ctx.item?.expStatusId);

                if (!canReview) {
                  return (
                    <ReadOnlyCell
                      value={
                        ctx.item?.expStatusAbbr ??
                        statusLabelByValue[value] ??
                        "Προς έλεγχο"
                      }
                    />
                  );
                }

                return (
                  <TextField
                    select
                    fullWidth
                    variant="standard"
                    size="small"
                    value={value}
                    onChange={(event) =>
                      handleStatusChange(ctx.item, event.target.value)
                    }
                    onClick={stopGridEvent}
                    onKeyDown={stopGridEvent}
                    InputProps={{ disableUnderline: true }}
                    SelectProps={{
                      SelectDisplayProps: {
                        "data-expense-review-editor": "true",
                        "data-expense-review-editor-order":
                          (ctx.row?.index ?? 0) * 10,
                        "data-expense-review-editor-type": "expStatus",
                      },
                    }}
                    sx={reviewInputSx}
                  >
                    {REVIEW_STATUS_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value || "pending"}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </FlexGridColumn>
        )}
        {showApprovedAmount && (
          <FlexGridColumn
            binding="approvedAmount"
            header="Εγκεκριμένο"
            width={150}
            align="right"
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const statusId = Number(ctx.item?.expStatusId);
                const isPartial =
                  statusId === REVIEW_STATUS_IDS.partiallyApproved;

                if (canReview && isPartial) {
                  return (
                    <ApprovedAmountEditor
                      value={ctx.item?.approvedAmount ?? ""}
                      grid={ctx.col?.grid}
                      editorOrder={(ctx.row?.index ?? 0) * 10 + 1}
                      onCommit={(value) =>
                        handleApprovedAmountCommit(ctx.item, value)
                      }
                    />
                  );
                }

                if (statusId === REVIEW_STATUS_IDS.approved) {
                  return (
                    <ReadOnlyCell
                      value={formatAmount(ctx.item?.totalAmount)}
                      align="right"
                    />
                  );
                }

                if (isPartial) {
                  return (
                    <ReadOnlyCell
                      value={formatAmount(ctx.item?.approvedAmount)}
                      align="right"
                    />
                  );
                }

                return <ReadOnlyCell value="-" align="right" muted />;
              }}
            />
          </FlexGridColumn>
        )}
        {showReviewStatus && (
          <FlexGridColumn
            binding="statusComments"
            header="Σχόλια Ελέγχου"
            width={260}
            minWidth={220}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                if (!canReview) {
                  return (
                    <ReadOnlyCell value={ctx.item?.statusComments} muted />
                  );
                }

                return (
                  <ReviewCommentsEditor
                    value={ctx.item?.statusComments ?? ""}
                    grid={ctx.col?.grid}
                    editorOrder={(ctx.row?.index ?? 0) * 10 + 2}
                    onCommit={(value) =>
                      handleReviewCommentsChange(ctx.item, value)
                    }
                  />
                );
              }}
            />
          </FlexGridColumn>
        )}
        {showAppartmentColumn && (
          <FlexGridColumn
            binding="appartmentName"
            header="Διαμέρισμα"
            width={180}
          />
        )}
        {showChildColumn && (
          <FlexGridColumn
            binding="childFullName"
            header="Ωφελούμενος"
            width={240}
          />
        )}
        {showEmployeeColumn && (
          <FlexGridColumn
            binding="employeeFullName"
            header="Εργαζόμενος"
            width={190}
          />
        )}
        {showDateFromColumn && (
          <FlexGridColumn
            binding="dateFrom"
            header="Από"
            format="dd/MM/yyyy"
            width={120}
            align="right"
          />
        )}
        {showDateToColumn && (
          <FlexGridColumn
            binding="dateTo"
            header="Έως"
            format="dd/MM/yyyy"
            width={120}
            align="right"
          />
        )}
        <FlexGridColumn binding="expComments" header="Σχόλια" width={220} />
        <FlexGridColumn
          binding="expRemarks"
          header="Παρατηρήσεις"
          width="*"
          minWidth={220}
        />
      </FlexGrid>

      {rows.length === 0 && (
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Δεν έχουν φορτωθεί γραμμές δαπάνης.
        </Typography>
      )}
    </Box>
  );
};

export default ExpenseDetailsGrid;
